import classNames from "classnames";
import React from "react";

import { Image } from "../../common/Image";
import { BreakPoint } from "../../models/screen.interfaces";
import { getViewportBreakpoint } from "../../utils/detectMobile";
import { CompatibilityPanel, ICompatibilityAccordionBlock } from "../models";

import styles from "./CompatibilityAccordion.module.scss";

interface IProps {
    cms: ICompatibilityAccordionBlock;
    contentPanels: CompatibilityPanel[];
}

interface IState {
    useIcon: boolean | null;
    activePanel: CompatibilityPanel | null;
}

// TODO(ssr, LEVEL 5 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 5/10 due to accordion state management, lifecycle method (componentDidMount), viewport breakpoint detection, conditional icon rendering, and responsive layout logic.
export class CompatibilityAccordion extends React.Component<IProps, IState> {
    state: IState = {
        useIcon: false,
        activePanel: null,
    };

    componentDidMount(): void {
        this.setState({
            useIcon: this.props.cms?.use_icon,
            activePanel: this.props.contentPanels[0],
        });
    }

    private clickPanel(panel: CompatibilityPanel): void {
        this.setState({ activePanel: panel });
    }

    private getIcon(compatibility: boolean): React.JSX.Element | undefined {
        if (!this.state.useIcon) {
            return;
        }
        const imgName = compatibility ? "icon_comp" : "icon_incomp";
        const altText = compatibility ? "compatible" : "incompatibile";

        return (
            <Image
                src={this.props.cms[`${imgName}`]?.url}
                alt={`${altText} icon`}
            />
        );
    }
    private buildPanel(panel: CompatibilityPanel): React.JSX.Element {
        const rowClassName = classNames({
            [styles.row]: true,
            [styles.rowIcon]: this.state.useIcon,
        });
        const triggerClassName = classNames({
            [styles.accordionPanelTrigger]: true,
            [styles.accordionPanelTriggerIsActive]:
                this.state.activePanel === panel,
        });

        const targetClassName = classNames({
            [styles.accordionPanelTarget]: true,
            [styles.compatibilityAccordionImage]:
                getViewportBreakpoint() < BreakPoint.LARGE,
            [styles.accordionPanelTargetIsActive]:
                this.state.activePanel === panel,
        });
        const isActivePanel = this.state.activePanel === panel ? true : false;
        return (
            <div className={rowClassName} key={panel.value.title}>
                <h3>
                    <button
                        className={triggerClassName}
                        onClick={() => {
                            if (this.state.activePanel !== panel) {
                                this.clickPanel(panel);
                            } else {
                                this.setState({ activePanel: null });
                            }
                        }}
                        id={`trigger_${panel.value.title.toLowerCase().replace(/\s/g, "")}`}
                        aria-controls={`panel_${panel.value.title.toLowerCase().replace(/\s/g, "")}`}
                        aria-expanded={isActivePanel}
                    >
                        {this.getIcon(panel.value.choice)} {panel.value.title}
                    </button>
                </h3>
                <div
                    className={targetClassName}
                    id={`panel_${panel.value.title.toLowerCase().replace(/\s/g, "")}`}
                >
                    <div
                        dangerouslySetInnerHTML={{
                            __html: panel.value.description || "",
                        }}
                    ></div>

                    {getViewportBreakpoint() < BreakPoint.MEDIUM && (
                        <Image
                            src={panel.value.image?.url}
                            alt={panel.value.title}
                            lazy={false}
                            className=""
                        />
                    )}
                </div>
            </div>
        );
    }

    render(): React.JSX.Element {
        return (
            <div className={styles.compatibilityAccordionContent}>
                <div
                    className={styles.compatibilityAccordionHeader}
                    dangerouslySetInnerHTML={{
                        __html: this.props.cms.compatibility_header,
                    }}
                ></div>
                {getViewportBreakpoint() >= BreakPoint.MEDIUM && (
                    <div
                        className={`${styles.compatibilityAccordionImage} ${styles.accordionPanelTarget} ${styles.accordionPanelTargetIsActive}`}
                    >
                        {this.state.activePanel && (
                            <Image
                                lazy={false}
                                className=""
                                src={this.state.activePanel?.value.image.url}
                                alt={this.state.activePanel?.value.title}
                            />
                        )}
                    </div>
                )}
                <div className={styles.compatibilityAccordionPanels}>
                    {this.props.contentPanels.map(
                        (panel: CompatibilityPanel) => {
                            return this.buildPanel(panel);
                        },
                    )}
                </div>
                <div
                    dangerouslySetInnerHTML={{
                        __html: this.props.cms.compatibility_footer || "",
                    }}
                ></div>
            </div>
        );
    }
}
